import { useEffect, useMemo, useState } from "react";
import {
  FolderOpen,
  MagnifyingGlass,
  HouseLine,
  Baby,
  CaretRight,
  ArrowLeft,
  WarningCircle,
  Clock,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabase";
import {
  listDocumentTypes,
  listFamilyDocuments,
  listChildDocuments,
  deleteFamilyDocument,
  deleteChildDocument,
  reviewDocument,
  buildDocumentChecklistRows,
  summariseDocumentChecklist,
} from "@/lib/documents";
import {
  uploadFamilyDocument,
  uploadChildDocument,
  familyDocumentSignedUrl,
  childDocumentSignedUrl,
} from "@/lib/storage";
import DocumentRequirementRow from "@/components/documents/DocumentRequirementRow";

/**
 * Admin · /admin/documents
 *
 * Two-column layout: left = family list with completion chips, right =
 * drill-down into the selected family's family-docs + each child's docs,
 * with admin approve / reject / revoke / delete on every row.
 */
export default function AdminDocuments() {
  const { profile } = useAuth();
  const [loadingList, setLoadingList] = useState(true);
  const [families, setFamilies] = useState([]); // enriched with summary
  const [search, setSearch] = useState("");
  const [selectedFamilyId, setSelectedFamilyId] = useState(null);
  const [familyTypes, setFamilyTypes] = useState([]);
  const [childTypes, setChildTypes] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  // 1. Load families + both sets of doc types. Re-runs when refreshKey bumps
  //    (used after FamilyDetail mutations so the KPIs stay current).
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingList(true);
      try {
        const orgId = profile?.organization_id;
        if (!orgId) return;

        const [fTypes, cTypes, fams] = await Promise.all([
          listDocumentTypes({ organizationId: orgId, scope: "family" }),
          listDocumentTypes({ organizationId: orgId, scope: "child" }),
          supabase
            .from("families")
            .select("id, name:display_name, organization_id")
            .eq("organization_id", orgId)
            .order("display_name"),
        ]);
        if (fams.error) throw fams.error;

        // Enrich each family with its summary (one pass per family —
        // acceptable for preview volumes; swap for a single RPC later).
        const enriched = await Promise.all(
          (fams.data || []).map(async (f) => {
            const [famDocs, kids] = await Promise.all([
              listFamilyDocuments(f.id),
              supabase
                .from("children")
                .select("id, first_name, last_name, organization_id, family_id")
                .eq("family_id", f.id),
            ]);
            const childrenRows = kids.data || [];
            const childDocs = [];
            for (const k of childrenRows) {
              // eslint-disable-next-line no-await-in-loop
              const cd = await listChildDocuments(k.id);
              childDocs.push(...cd);
            }
            const familyRows = buildDocumentChecklistRows(fTypes, famDocs);
            const childRows = buildDocumentChecklistRows(
              // Repeat types for each child so summary totals reflect per-child requirements.
              childrenRows.flatMap(() => cTypes.map((t) => ({ ...t, _child: true }))),
              childDocs.map((d) => ({
                ...d,
                document_type_id: d.document_type_id,
              })),
            );
            const allRows = [...familyRows, ...childRows];
            const summary = summariseDocumentChecklist(allRows);
            return { ...f, childrenCount: childrenRows.length, summary };
          }),
        );

        if (!alive) return;
        setFamilyTypes(fTypes);
        setChildTypes(cTypes);
        setFamilies(enriched);
      } catch (err) {
        toast.error(err.message || "Could not load families");
      } finally {
        if (alive) setLoadingList(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [profile?.organization_id, refreshKey]);

  const filteredFamilies = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return families;
    return families.filter((f) => (f.name || "").toLowerCase().includes(s));
  }, [families, search]);

  const kpis = useMemo(() => {
    const totalFamilies = families.length;
    const incomplete = families.filter(
      (f) => f.summary.missingRequired > 0 || f.summary.expiredRequired > 0 || f.summary.rejectedCount > 0,
    ).length;
    const pendingReview = families.reduce(
      (sum, f) => sum + (f.summary.uploadedCount || 0),
      0,
    );
    const expiring = families.reduce(
      (sum, f) => sum + (f.summary.expiringRequired || 0),
      0,
    );
    return { totalFamilies, incomplete, pendingReview, expiring };
  }, [families]);

  return (
    <div data-testid="admin-documents" className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
            Compliance
          </p>
          <h1 className="mt-1 font-heading text-2xl font-semibold tracking-tight text-neutral-900">
            Documents & forms
          </h1>
          <p className="mt-1 text-sm text-neutral-600">
            Review uploads, approve or reject paperwork, and track expiring
            records across every family.
          </p>
        </div>
      </header>

      <section
        data-testid="admin-documents-kpis"
        className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
      >
        <Kpi testid="admin-documents-kpi-families" label="Families" value={kpis.totalFamilies} />
        <Kpi
          testid="admin-documents-kpi-pending"
          label="Pending review"
          value={kpis.pendingReview}
          tone={kpis.pendingReview > 0 ? "accent" : "neutral"}
        />
        <Kpi
          testid="admin-documents-kpi-incomplete"
          label="Incomplete"
          value={kpis.incomplete}
          tone={kpis.incomplete > 0 ? "warning" : "neutral"}
        />
        <Kpi
          testid="admin-documents-kpi-expiring"
          label="Expiring soon"
          value={kpis.expiring}
          tone={kpis.expiring > 0 ? "warning" : "neutral"}
        />
      </section>

      {selectedFamilyId ? (
        <FamilyDetail
          familyId={selectedFamilyId}
          familyTypes={familyTypes}
          childTypes={childTypes}
          onBack={() => {
            setSelectedFamilyId(null);
            setRefreshKey((k) => k + 1);
          }}
        />
      ) : (
        <>
          <div className="flex items-center gap-2 rounded-2xl border border-neutral-200 bg-white px-3 py-2">
            <MagnifyingGlass size={16} weight="bold" className="text-neutral-400" />
            <input
              data-testid="admin-documents-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search families…"
              className="flex-1 bg-transparent text-sm focus:outline-none"
            />
          </div>

          {loadingList ? (
            <div className="flex items-center justify-center py-20">
              <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
            </div>
          ) : filteredFamilies.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50 p-8 text-center text-sm text-neutral-500">
              No families match your search.
            </p>
          ) : (
            <ul data-testid="admin-documents-family-list" className="space-y-2">
              {filteredFamilies.map((f) => (
                <li key={f.id}>
                  <button
                    type="button"
                    data-testid={`admin-documents-family-row-${f.id}`}
                    data-percent={f.summary.percentComplete}
                    onClick={() => setSelectedFamilyId(f.id)}
                    className="flex w-full items-center gap-4 rounded-2xl border border-neutral-200 bg-white p-4 text-left transition-all hover:-translate-y-[1px] hover:shadow-sm"
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                      <FolderOpen size={18} weight="duotone" className="text-primary-hover" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-heading text-sm font-semibold text-neutral-900">
                        {f.name}
                      </p>
                      <p className="text-xs text-neutral-500">
                        {f.childrenCount} child{f.childrenCount === 1 ? "" : "ren"}
                        {f.summary.uploadedCount > 0 && (
                          <> · <span className="text-neutral-900 font-medium">{f.summary.uploadedCount} pending</span></>
                        )}
                        {f.summary.missingRequired > 0 && (
                          <> · <span className="text-[#C6851E] font-medium">{f.summary.missingRequired} missing</span></>
                        )}
                        {f.summary.expiredRequired > 0 && (
                          <> · <span className="text-[#C6851E] font-medium">{f.summary.expiredRequired} expired</span></>
                        )}
                        {f.summary.expiringRequired > 0 && (
                          <> · <span className="text-amber-700 font-medium">{f.summary.expiringRequired} expiring</span></>
                        )}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">
                        Complete
                      </p>
                      <p className="font-heading text-lg font-semibold text-neutral-900">
                        {f.summary.percentComplete}%
                      </p>
                    </div>
                    <CaretRight size={16} weight="bold" className="text-neutral-400" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function Kpi({ testid, label, value, tone = "neutral" }) {
  const toneMap = {
    neutral: "text-neutral-900",
    accent: "text-primary-hover",
    warning: "text-[#C6851E]",
  };
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border border-neutral-200 bg-white p-4"
    >
      <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
        {label}
      </p>
      <p className={`mt-1 font-heading text-2xl font-semibold ${toneMap[tone]}`}>
        {value}
      </p>
    </div>
  );
}

function FamilyDetail({ familyId, familyTypes, childTypes, onBack }) {
  const [loading, setLoading] = useState(true);
  const [family, setFamily] = useState(null);
  const [children, setChildren] = useState([]);
  const [familyDocs, setFamilyDocs] = useState([]);
  const [childDocsByChildId, setChildDocsByChildId] = useState({});

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data: fam, error } = await supabase
          .from("families")
          .select("id, name:display_name, organization_id")
          .eq("id", familyId)
          .maybeSingle();
        if (error) throw error;
        const { data: kids, error: cErr } = await supabase
          .from("children")
          .select("id, first_name, last_name, organization_id, family_id, withdrawn_at")
          .eq("family_id", familyId)
          .order("first_name");
        if (cErr) throw cErr;
        const fDocs = await listFamilyDocuments(familyId);
        const docsByChild = {};
        for (const kid of kids || []) {
          // eslint-disable-next-line no-await-in-loop
          docsByChild[kid.id] = await listChildDocuments(kid.id);
        }
        if (!alive) return;
        setFamily(fam);
        setChildren(kids || []);
        setFamilyDocs(fDocs);
        setChildDocsByChildId(docsByChild);
      } catch (err) {
        toast.error(err.message || "Could not load family");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [familyId]);

  const familyRows = useMemo(
    () => buildDocumentChecklistRows(familyTypes, familyDocs),
    [familyTypes, familyDocs],
  );

  async function refreshFamilyDocs() {
    const docs = await listFamilyDocuments(familyId);
    setFamilyDocs(docs);
  }
  async function refreshChildDocs(childId) {
    const docs = await listChildDocuments(childId);
    setChildDocsByChildId((prev) => ({ ...prev, [childId]: docs }));
  }

  if (loading || !family) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-documents-family-detail" className="space-y-6">
      <div className="flex items-center gap-3">
        <button
          type="button"
          data-testid="admin-documents-back"
          onClick={onBack}
          className="flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
        >
          <ArrowLeft size={14} weight="bold" />
          All families
        </button>
        <h2 className="font-heading text-xl font-semibold text-neutral-900">
          {family.name}
        </h2>
      </div>

      <section>
        <div className="mb-3 flex items-center gap-2">
          <HouseLine size={16} weight="duotone" className="text-primary-hover" />
          <h3 className="font-heading text-base font-semibold text-neutral-900">
            Family documents
          </h3>
        </div>
        <div className="space-y-4">
          {familyRows.map((row) => (
            <DocumentRequirementRow
              key={row.type.id}
              row={row}
              role="admin"
              onUpload={async (file) => {
                await uploadFamilyDocument({
                  familyId,
                  organizationId: family.organization_id,
                  documentTypeId: row.type.id === "__other__" ? null : row.type.id,
                  file,
                });
                await refreshFamilyDocs();
              }}
              onDelete={async (doc) => {
                await deleteFamilyDocument({ id: doc.id, storagePath: doc.storage_path });
                await refreshFamilyDocs();
              }}
              onReview={async (doc, status, note) => {
                await reviewDocument({
                  scope: "family",
                  documentId: doc.id,
                  status,
                  note,
                });
                await refreshFamilyDocs();
              }}
              onDownload={async (doc) =>
                doc.storage_path ? familyDocumentSignedUrl(doc.storage_path) : null
              }
            />
          ))}
        </div>
      </section>

      {children.map((kid) => (
        <ChildSection
          key={kid.id}
          child={kid}
          types={childTypes}
          docs={childDocsByChildId[kid.id] || []}
          onRefresh={() => refreshChildDocs(kid.id)}
        />
      ))}
    </div>
  );
}

function ChildSection({ child, types, docs, onRefresh }) {
  const rows = useMemo(() => buildDocumentChecklistRows(types, docs), [types, docs]);
  return (
    <section data-testid={`admin-documents-child-${child.id}`}>
      <div className="mb-3 flex items-center gap-2">
        <Baby size={16} weight="duotone" className="text-primary-hover" />
        <h3 className="font-heading text-base font-semibold text-neutral-900">
          {child.first_name} {child.last_name || ""}
        </h3>
      </div>
      <div className="space-y-4">
        {rows.map((row) => (
          <DocumentRequirementRow
            key={row.type.id}
            row={row}
            role="admin"
            onUpload={async (file) => {
              await uploadChildDocument({
                childId: child.id,
                organizationId: child.organization_id,
                documentTypeId: row.type.id === "__other__" ? null : row.type.id,
                file,
              });
              await onRefresh();
            }}
            onDelete={async (doc) => {
              await deleteChildDocument({ id: doc.id, storagePath: doc.storage_path });
              await onRefresh();
            }}
            onReview={async (doc, status, note) => {
              await reviewDocument({
                scope: "child",
                documentId: doc.id,
                status,
                note,
              });
              await onRefresh();
            }}
            onDownload={async (doc) =>
              doc.storage_path ? childDocumentSignedUrl(doc.storage_path) : null
            }
          />
        ))}
      </div>
    </section>
  );
}
